<template>
  <div class="tag-selector" ref="trigger" @click="isOpen = !isOpen">
    <div class="current-tag" :class="{ 'has-tag': tag }">
      <div class="tag-color-dot" :style="tag ? { backgroundColor: tag.color } : {}">
        <vue-feather type="tag" size="11" />
      </div>
      <span>{{ tag ? tag.name : 'No tag' }}</span>
      <span v-if="tag && tagSource === 'ai'" class="ai-tag-indicator" v-tooltip="aiTagTooltip">
        <img src="@/assets/icons/stars.png" alt="AI" class="ai-tag-icon" />
      </span>
      <AiFeedbackThumbs v-if="feedbackEnabled && tag && tagSource === 'ai'" :rating="feedbackRating" :size="12"
        @feedback="$emit('feedback', $event)" />
    </div>
    <FloatingDropdown :open="isOpen" :triggerRef="$refs.trigger" :matchWidth="false" @dismiss="isOpen = false">
      <div class="tag-dropdown">
        <div class="tag-search-wrapper" v-if="searchable" @click.stop>
          <input ref="searchInput" class="tag-search-input" v-model="searchQuery"
            placeholder="Search tags..." autocomplete="nope" data-lpignore="true" data-form-type="other" />
        </div>
        <div class="tag-list-item" :data-active="!tag" @click.stop="selectTag('')">
          <div class="tag-icon-circle">
            <vue-feather type="tag" size="12" />
          </div>
          <span>No Tag</span>
        </div>
        <div class="tag-list-item" v-for="t of filteredTags" :key="t.id"
          :data-active="tag?.id == t.id" @click.stop="selectTag(t.id)">
          <div class="tag-icon-circle" :style="{ backgroundColor: t.color }">
            <vue-feather type="tag" size="12" />
          </div>
          <span>{{ t.name }}</span>
        </div>
      </div>
    </FloatingDropdown>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import { FloatingDropdown } from '@/design'
// Imported directly (not via @/design): the index re-exports TagSelector before
// AiFeedbackThumbs, so the barrel binding is uninitialized during webpack's
// circular evaluation of this module
import AiFeedbackThumbs from './AiFeedbackThumbs.vue'

export default {
  name: 'TagSelector',
  components: { VueFeather, FloatingDropdown, AiFeedbackThumbs },
  props: {
    tag: { type: Object, default: null },
    tags: { type: Array, required: true },
    tagSource: { type: String, default: null },
    aiTagExplanation: { type: String, default: null },
    searchable: { type: Boolean, default: true },
    feedbackEnabled: { type: Boolean, default: false },
    feedbackRating: { type: String, default: null }
  },
  emits: ['select', 'feedback'],
  data() {
    return {
      isOpen: false,
      searchQuery: ''
    }
  },
  computed: {
    filteredTags() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return this.tags;
      return this.tags.filter(t => t.name.toLowerCase().includes(q));
    },
    aiTagTooltip() {
      // The indicator only renders when a real tag is present, so this is always an AI suggestion.
      let tooltip = 'AI Suggested';
      if (this.aiTagExplanation) {
        tooltip += `\n${this.aiTagExplanation}`;
      }
      return tooltip;
    }
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.searchQuery = '';
        this.$nextTick(() => this.$refs.searchInput?.focus());
      }
    }
  },
  mounted() {
    window.addEventListener('mousedown', this.handleClickOutside);
  },
  unmounted() {
    window.removeEventListener('mousedown', this.handleClickOutside);
  },
  methods: {
    selectTag(tagId) {
      this.isOpen = false;
      this.$emit('select', tagId);
    },
    handleClickOutside(event) {
      if (!this.isOpen) return;
      const trigger = this.$refs.trigger;
      if (trigger && trigger.contains(event.target)) return;
      this.isOpen = false;
    }
  }
}
</script>

<style scoped>
.tag-selector {
  position: relative;
}

.current-tag {
  display: flex;
  align-items: center;
  gap: 5px;
  border-radius: 4px;
  padding: 4px 10px 4px 5px;
  border: 1px solid #d1d5db;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.8125rem;
  white-space: nowrap;
  color: #374151;
  background: #f3f4f6;
}

.current-tag:hover {
  background: #e5e7eb;
}

.tag-color-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #FFF;
}

.tag-icon-circle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #FFF;
}

.tag-dropdown {
  background: #FFF;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  overflow-y: auto;
  max-height: 300px;
  width: 200px;
  padding: 4px;
}

.tag-search-wrapper {
  padding: 4px 4px 0;
}

.tag-search-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 4px;
}

.tag-search-input:focus {
  border-color: var(--color-primary);
}

.tag-list-item {
  padding: 7px 10px;
  gap: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  transition: background 0.1s ease;
}

.tag-list-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-list-item:hover {
  background: #f3f4f6;
}

.tag-list-item[data-active="true"] {
  background: #e9edff;
}

.ai-tag-indicator {
  display: flex;
  align-items: center;
  cursor: help;
}

.ai-tag-icon {
  width: 14px;
  height: 14px;
}
</style>
